import React, { useState } from 'react';
import { themeService, Theme } from '../services/themeService';
import '../styles/InsightModal.css';
import '../styles/AddThemeModal.css';

interface EditThemeModalProps {
  theme: Theme;
  /** Parent themes available to (re)assign this theme under. */
  parentThemes: Theme[];
  onClose: () => void;
  onSaved: (theme: Theme) => void;
}

/* Lightweight editor for an existing theme — name, description and (optional)
   parent. Mirrors the AddThemeModal styling but is a single step. */
const EditThemeModal: React.FC<EditThemeModalProps> = ({ theme, parentThemes, onClose, onSaved }) => {
  const [name, setName] = useState(theme.name);
  const [description, setDescription] = useState(theme.description ?? '');
  const [parentId, setParentId] = useState<string | null>(theme.parent_id);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Can't parent a theme under itself.
  const parentOptions = parentThemes.filter(p => p.id !== theme.id);

  const save = async () => {
    if (!name.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await themeService.updateTheme(theme.id, {
        name: name.trim(),
        description: description.trim() || null,
        parent_id: parentId,
      });
      onSaved(updated);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save theme');
      setSaving(false);
    }
  };

  return (
    <div className="cum-modal-backdrop" onClick={onClose}>
      <div className="inline-edit-form premium-task-modal cum-company-modal prm-submodal" onClick={e => e.stopPropagation()}>
        <div className="task-modal-header">
          <div className="header-titles-new"><h3>Edit theme</h3></div>
          <button type="button" className="task-modal-close" onClick={onClose} title="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>

        <div className="prm-submodal-body">
          <div className="prm-submodal-field">
            <label className="prm-submodal-label">Name</label>
            <input className="prm-submodal-input" type="text" placeholder="Theme name…" value={name} onChange={e => setName(e.target.value)} autoFocus />
          </div>

          <div className="prm-submodal-field">
            <label className="prm-submodal-label">Description</label>
            <textarea className="prm-submodal-textarea" placeholder="Describe this theme…" value={description} onChange={e => setDescription(e.target.value)} rows={4} />
          </div>

          <div className="prm-submodal-field">
            <label className="prm-submodal-label">Parent theme</label>
            <select className="prm-submodal-input" value={parentId ?? ''} onChange={e => setParentId(e.target.value || null)}>
              <option value="">No parent (top-level)</option>
              {parentOptions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          {error && <div className="prm-submodal-field" style={{ color: '#dc2626', fontSize: 13 }}>{error}</div>}
        </div>

        <div className="task-modal-footer">
          <button className="cancel-btn" onClick={onClose}>Cancel</button>
          <button className="submit-btn" onClick={save} disabled={!name.trim() || saving}>{saving ? 'Saving…' : 'Save changes'}</button>
        </div>
      </div>
    </div>
  );
};

export default EditThemeModal;
